import React, { useEffect, useMemo, useState, useRef } from 'react';
import moment from 'moment';
import './SessionTimeRange.scss';
import TextInput from '../TextInput';
import Label from '../Label';

const SessionTimeRange = ({
  isAgenda = false,
  startLabel,
  endLabel,
  durationLabel,
  startId,
  endId,
  onDurationChange,
  startValue,
  endValue,
  onStartChange,
  onEndChange,
  startError,
  endError,
  disabledStart = false,
  disabledEnd = false,
  showDuration = true,
}) => {
  const [startInput, setStartInput] = useState('');
  const [startMeridiem, setStartMeridiem] = useState('AM');
  const [endInput, setEndInput] = useState('');
  const [endMeridiem, setEndMeridiem] = useState('AM');

  const lastDurationRef = useRef('');

  /* ---------- helpers ---------- */

  const isValid12HourTime = (value) => {
    if (!/^\d{2}:\d{2}$/.test(value)) return false;
    const [hh, mm] = value.split(':').map(Number);
    if (hh < 1 || hh > 12) return false;
    if (mm < 0 || mm > 59) return false;
    return true;
  };

  const withAutoColon = (value) => {
    const clean = value.replace(':', '');
    if (!/^\d*$/.test(clean)) return value;

    if (clean.length === 4) {
      return `${clean.slice(0, 2)}:${clean.slice(2)}`;
    }
    return value;
  };

  const to24Hour = (time, meridiem) => {
    if (!isValid12HourTime(time)) return '';
    const m = moment(`${time} ${meridiem}`, 'hh:mm A', true);
    return m.isValid() ? m.format('HH:mm') : '';
  };

  const toDisplayTime = (value) => {
    if (!value) return { time: '', meridiem: 'AM' };
    const m = moment(value, ['HH:mm', 'HH:mm:ss'], true);
    if (!m.isValid()) return { time: '', meridiem: 'AM' };
    return { time: m.format('hh:mm'), meridiem: m.format('A') };
  };

  const emitIfChanged = (next, current, cb) => {
    if (!next || next === current) return;
    cb?.(next);
  };

  const normalizeTime = (value) =>
    isValid12HourTime(value) ? value : '';

  /* ---------- sync external values ---------- */

  useEffect(() => {
    const { time, meridiem } = toDisplayTime(startValue);
    setStartInput(time);
    setStartMeridiem(meridiem);
  }, [startValue]);

  useEffect(() => {
    const { time, meridiem } = toDisplayTime(endValue);
    setEndInput(time);
    setEndMeridiem(meridiem);
  }, [endValue]);

  /* ---------- input handlers ---------- */

  const handleStartInputChange = (value) => {
    const next = withAutoColon(value);
    if (!/^[0-9:]*$/.test(next)) return;
    setStartInput(next);

    if (next.length === 5 && isValid12HourTime(next)) {
      emitIfChanged(
        to24Hour(next, startMeridiem),
        startValue,
        onStartChange
      );
    }
  };

  const handleEndInputChange = (value) => {
    const next = withAutoColon(value);
    if (!/^[0-9:]*$/.test(next)) return;
    setEndInput(next);

    if (next.length === 5 && isValid12HourTime(next)) {
      emitIfChanged(
        to24Hour(next, endMeridiem),
        endValue,
        onEndChange
      );
    }
  };

  const handleStartMeridiemChange = (value) => {
    setStartMeridiem(value);
    emitIfChanged(
      to24Hour(startInput, value),
      startValue,
      onStartChange
    );
  };

  const handleEndMeridiemChange = (value) => {
    setEndMeridiem(value);
    emitIfChanged(
      to24Hour(endInput, value),
      endValue,
      onEndChange
    );
  };

  /* ---------- duration ---------- */

  const durationText = useMemo(() => {
    if (!showDuration) return '';

    const start24 = to24Hour(startInput, startMeridiem);
    const end24 = to24Hour(endInput, endMeridiem);

    if (!start24 || !end24) return '';

    const diff = moment(end24, 'HH:mm').diff(
      moment(start24, 'HH:mm'),
      'minutes'
    );

    if (diff <= 0) return '';

    const h = Math.floor(diff / 60);
    const m = diff % 60;

    if (h && m) return `${h} hr ${m} min`;
    if (h) return `${h} hr`;
    return `${m} min`;
  }, [startInput, startMeridiem, endInput, endMeridiem, showDuration]);

  const durationError = useMemo(() => {
    const start24 = to24Hour(startInput, startMeridiem);
    const end24 = to24Hour(endInput, endMeridiem);

    if (!start24 || !end24) return '';

    return moment(end24, 'HH:mm').isAfter(moment(start24, 'HH:mm'))
      ? ''
      : 'End Time must be greater than Start Time.';
  }, [startInput, startMeridiem, endInput, endMeridiem]);

  useEffect(() => {
    if (!showDuration) return;
    if (lastDurationRef.current === durationText) return;
    lastDurationRef.current = durationText;
    onDurationChange?.(durationText);
  }, [durationText, showDuration]);

  /* ---------- UI ---------- */

  const renderField = ({
    id,
    value,
    onChange,
    onBlur,
    meridiem,
    onMeridiemChange,
    error,
    disabled,
    label,
  }) => (
    <div className="session-time-field">
      {!isAgenda && label && <Label label={label} />}
      <div className="session-time-field__controls">
        {isAgenda && label && (
          <Label label={label} className="text-14-400-18 text-nowrap" />
        )}

        <div className="session-time-field__input-wrapper">
          <i className="bi bi-clock session-time-field__icon" />
          <input
            id={id}
            className="session-time-field__input"
            inputMode="numeric"
            maxLength={5}
            placeholder="hh:mm"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={() => onBlur(normalizeTime(value))}
            disabled={disabled}
          />
        </div>

        <select
          className="session-time-field__meridiem"
          value={meridiem}
          onChange={(e) => onMeridiemChange(e.target.value)}
          disabled={disabled}
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>

      <span className="session-time-field__error">
        {error || ''}
      </span>
    </div>
  );

  return (
    <div className="session-time-range">
      <div className="row justify-content-between align-items-center g-2">
        <div className="col-12 col-lg-5 col-md-3">
          {renderField({
            id: startId,
            value: startInput,
            onChange: handleStartInputChange,
            onBlur: setStartInput,
            meridiem: startMeridiem,
            onMeridiemChange: handleStartMeridiemChange,
            error: startError,
            disabled: disabledStart,
            label: startLabel,
          })}
        </div>

        <div className="col-12 col-lg-5 col-md-3">
          {renderField({
            id: endId,
            value: endInput,
            onChange: handleEndInputChange,
            onBlur: setEndInput,
            meridiem: endMeridiem,
            onMeridiemChange: handleEndMeridiemChange,
            error: endError || durationError,
            disabled: disabledEnd,
            label: endLabel,
          })}
        </div>

        <div className="col-12 col-lg-2 col-md-6 cmb-20">
          <TextInput
            value={durationText}
            disabled
            label={durationLabel}
            placeholder={durationText ? '' : '_hr _min'}
          />
        </div>
      </div>
    </div>
  );
};

export default SessionTimeRange;
